import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import Avatar from "../../Avatar";
import Flex from "../../Flex";
import Popover from "../../Popover";
import { H6 } from "../../Typography";
import TeamTypeBar from "../components/TeamTypeBar";
import { TeamShape } from "../types/sideNav";
import {
  SectionItem,
  SectionItemList,
  SectionItemName,
  SectionResponsiveContainer,
  SectionTeamLink,
  CollapsedAvatarWrapper,
  CollapsedItemLink,
  CollapsedItemWrapper,
} from "../styles/sideNavSection.styles";

/**
 * SideNavTeamsSection - "My Teams" section of the side navigation.
 *
 * Renders the rich team-row layout natively (avatar + optional team-type bar
 * + name + optional popover badge). Consumers no longer need to pass a
 * custom `renderTeamsSection`; just enrich each team with the optional
 * `badgeVariant`, `type`, and `popoverBadge` fields described below.
 *
 * Requires a `react-router-dom` `<Router>` ancestor: each team row uses
 * `<Link>` for navigation and the active-state highlight is derived from
 * `useLocation()`. The same applies to `SideNavPinnedSection`. (This is
 * already the case for the rest of `SideNav`, e.g. `NavItem` and
 * `SideNavCurrentViewSection`.)
 *
 * @param {Array} teams - Team items
 * @param {string} teams[].name - Display name
 * @param {string} teams[].link - Route path
 * @param {string} [teams[].avatar] - Avatar image URL
 * @param {string} [teams[].gradient] - Avatar gradient fallback
 * @param {string} [teams[].type] - Team type label (e.g. "Chapter")
 * @param {string} [teams[].badgeVariant] - Token mapping for the team-type bar
 * @param {React.ReactNode} [teams[].popoverBadge] - Badge node rendered inside the row tooltip
 * @param {string|number} [teams[].id] - Stable React key
 * @param {boolean} isExpanded - Whether the side nav is in its expanded state
 */
const SideNavTeamsSection = ({ teams, isExpanded }) => {
  const location = useLocation();

  if (!teams || teams.length === 0) {
    return null;
  }

  const renderTeamItem = (team) => {
    const {
      avatar,
      name,
      link,
      gradient,
      type,
      badgeVariant = "default",
      popoverBadge,
      id,
    } = team;

    if (!name || !link) {
      return null;
    }

    const hasTeamType = Boolean(type);
    const isActive = link && location.pathname.startsWith(link);

    const popoverContent = popoverBadge ? (
      <Flex gap="s" alignItems="center">
        <span>{name}</span>
        {popoverBadge}
      </Flex>
    ) : (
      <span>{name}</span>
    );

    if (isExpanded) {
      return (
        <SectionItem
          key={id || name}
          isExpanded={isExpanded}
          className={isActive ? "active" : ""}
        >
          <Popover
            text={popoverContent}
            direction="right"
            width="fit-content"
            tabIndex="-1"
            inlineBlock={false}
            style={{ width: "100%", display: "flex", flex: 1 }}
          >
            <SectionTeamLink to={link} aria-label={name} data-team-link="true">
              <CollapsedAvatarWrapper>
                <Avatar
                  image={avatar}
                  customSize="24px"
                  shape="square"
                  gradient={gradient}
                />
              </CollapsedAvatarWrapper>
              {hasTeamType && <TeamTypeBar badgeVariant={badgeVariant} />}
              <SectionItemName>{name}</SectionItemName>
            </SectionTeamLink>
          </Popover>
        </SectionItem>
      );
    }

    return (
      <SectionItem key={id || name} isExpanded={isExpanded}>
        <Popover
          text={popoverContent}
          direction="right"
          width="fit-content"
          tabIndex="-1"
        >
          <CollapsedItemWrapper className={isActive ? "active" : ""}>
            <CollapsedItemLink to={link} aria-label={name}>
              <CollapsedAvatarWrapper>
                <Avatar
                  image={avatar}
                  customSize="24px"
                  shape="square"
                  gradient={gradient}
                />
              </CollapsedAvatarWrapper>
              {hasTeamType && <TeamTypeBar badgeVariant={badgeVariant} />}
            </CollapsedItemLink>
          </CollapsedItemWrapper>
        </Popover>
      </SectionItem>
    );
  };

  return (
    <SectionResponsiveContainer
      flexDirection="column"
      gap="r"
      my={isExpanded ? "s" : "0"}
    >
      {isExpanded && (
        <H6 sizing="small" color="greyDark">
          My Teams
        </H6>
      )}
      <SectionItemList isExpanded={isExpanded}>
        {teams.map(renderTeamItem)}
      </SectionItemList>
    </SectionResponsiveContainer>
  );
};

SideNavTeamsSection.propTypes = {
  teams: PropTypes.arrayOf(PropTypes.shape(TeamShape)),
  isExpanded: PropTypes.bool,
};

export default SideNavTeamsSection;
